/**
 * Shared compact event list item used in detail-panel and country-panel.
 * Displays severity dot, title, and relative time.
 */

import { eventBus } from '@/core/events';
import { SEVERITY_COLORS } from '@/utils/severity';
import { relativeTime } from '@/utils/time';
import type { NormalizedEvent } from '@/sources/base-source';

/**
 * Create a compact event list item element.
 * Emits event:selected on click, then calls the optional onClick callback.
 */
export function createCompactEventItem(
  event: NormalizedEvent,
  onClick?: (event: NormalizedEvent) => void,
): HTMLElement {
  const item = document.createElement('div');
  item.setAttribute('role', 'button');
  item.setAttribute('tabindex', '0');
  item.setAttribute('aria-label', `${event.severity ?? 'info'}: ${event.title}`);
  item.style.cssText = `
    display: flex; align-items: center; gap: 6px;
    padding: 4px 0; cursor: pointer; font-size: 11px;
    border-bottom: 1px solid var(--border-primary);
  `;

  // Severity dot
  const dot = document.createElement('div');
  const sev = event.severity ?? 'info';
  dot.style.cssText = `
    width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
    background: ${SEVERITY_COLORS[sev] ?? SEVERITY_COLORS['info']};
  `;
  item.appendChild(dot);

  // Title
  const title = document.createElement('span');
  title.style.cssText = 'flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-primary);';
  title.textContent = event.title;
  item.appendChild(title);

  // Time
  const time = document.createElement('span');
  time.style.cssText = 'flex-shrink: 0; color: var(--text-muted); font-size: 10px;';
  time.textContent = relativeTime(event.timestamp);
  item.appendChild(time);

  // Click and keyboard handler
  const activate = () => {
    eventBus.emit('event:selected', event);
    onClick?.(event);
  };
  item.onclick = activate;
  item.onkeydown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };

  return item;
}
